const STORAGE_KEY = "neozapp:query-cache:v1";

const PERSISTABLE_QUERY_PREFIXES = new Set([
  "users",
  "queues",
  "tags",
  "prompts",
  "queueIntegrations",
  "quickMessages",
  "dashboard",
  "campaignSettings",
]);

const getPersistConfig = (query) => query?.options?.meta?.persist;

const shouldPersistQuery = (query) => {
  const queryKey = query?.queryKey || [];
  const prefix = queryKey[0];
  const persistConfig = getPersistConfig(query);

  if (!prefix || !PERSISTABLE_QUERY_PREFIXES.has(prefix)) {
    return false;
  }

  if (persistConfig === false) {
    return false;
  }

  return query.state?.data !== undefined;
};

const getCurrentTimestamp = () => Date.now();

const buildPersistedEntry = (query) => {
  const persistConfig = getPersistConfig(query) || {};

  return {
    queryKey: query.queryKey,
    data: query.state.data,
    updatedAt: query.state.dataUpdatedAt || getCurrentTimestamp(),
    maxAge: persistConfig.maxAge || null,
  };
};

const loadPersistedEntries = () => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.entries) ? parsed.entries : [];
  } catch (_error) {
    return [];
  }
};

const savePersistedEntries = (entries) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        version: 1,
        savedAt: getCurrentTimestamp(),
        entries,
      })
    );
  } catch (_error) {
    // best-effort local persistence
  }
};

const isEntryExpired = (entry) => {
  if (!entry?.maxAge) {
    return false;
  }

  return getCurrentTimestamp() - entry.updatedAt > entry.maxAge;
};

export const restorePersistedQueryCache = (queryClient) => {
  loadPersistedEntries()
    .filter((entry) => entry?.queryKey && !isEntryExpired(entry))
    .forEach((entry) => {
      queryClient.setQueryData(entry.queryKey, entry.data, { updatedAt: entry.updatedAt });
    });
};

export const setupQueryCachePersistence = (queryClient) => {
  if (typeof window === "undefined") {
    return () => {};
  }

  let frameId = null;

  const persist = () => {
    const entries = queryClient
      .getQueryCache()
      .getAll()
      .filter(shouldPersistQuery)
      .map(buildPersistedEntry);

    savePersistedEntries(entries);
  };

  const unsubscribe = queryClient.getQueryCache().subscribe(() => {
    if (frameId) {
      window.cancelAnimationFrame(frameId);
    }

    frameId = window.requestAnimationFrame(() => {
      persist();
      frameId = null;
    });
  });

  persist();

  return () => {
    if (frameId) {
      window.cancelAnimationFrame(frameId);
    }
    unsubscribe();
  };
};

export const clearPersistedQueryCache = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(STORAGE_KEY);
};
